import {skipToken} from '@reduxjs/toolkit/query';

import {find, findLast, head, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  BusinessCaseResponseBody,
  TenantBankAccount,
  useGetBusinessCaseQuery,
  useGetCheckoutV2Query,
} from '@dms/api/sales';
import {combineAccountNumberAndBankCode, useBank} from '@dms/shared';

import {Nullish} from 'shared/web';

import {useIsSellerAsSupplier} from './useIsSellerAsSupplier';

type Bank = ReturnType<typeof useBank>;
type BankAccountOption = Bank['salesBankAccountOptions'][number];

interface PaymentTenantBankAccountsArgs {
  checkoutId: string;
  /** The payment's own business case, nil while it loads. */
  businessCase: Pick<BusinessCaseResponseBody, 'brokerageBusinessCaseId'> | Nullish;
  currency: string | Nullish;
}

/**
 * The accounts an issued payment document can name as the supplier's (`tenantBankAccount`). Callers
 * consume one interface; only this hook knows which source the current mode uses:
 * - dealer mode: sales accounts of the active branch, keyed by account number,
 * - seller-as-supplier (mode 2): the seller's accounts from the commission purchase, keyed by id.
 */
export function usePaymentTenantBankAccounts(args: PaymentTenantBankAccountsArgs) {
  const sellerAsSupplier = useIsSellerAsSupplier(args.checkoutId);
  const {isSellerAsSupplier} = sellerAsSupplier;
  // No branch is involved in mode 2, so its accounts are not loaded then.
  const bank = useBank(isSellerAsSupplier ? null : undefined);
  const seller = useSellerBankAccounts({
    businessCase: isSellerAsSupplier ? args.businessCase : null,
    bank,
  });

  const branchDefaultValue =
    bank.getSalesDefaultBankAccount(args.currency ?? undefined)?.accountNumber ??
    head(bank.salesBankAccountOptions)?.value ??
    null;

  return {
    isSellerAsSupplier,
    /** Changes with the account source, so a form can remount instead of keeping a stale default. */
    source: isSellerAsSupplier ? ('seller' as const) : ('active-branch' as const),
    options: isSellerAsSupplier ? seller.options : bank.salesBankAccountOptions,
    defaultValue: isSellerAsSupplier ? seller.defaultValue : branchDefaultValue,
    getTenantBankAccount: isSellerAsSupplier
      ? seller.getTenantBankAccount
      : (accountNumber: string | Nullish) =>
          toTenantBankAccount(bank.getBankAccountByNumber(accountNumber ?? '')),
    isLoading: sellerAsSupplier.isLoading || bank.isLoading || seller.isLoading,
    // The seller's accounts only matter in mode 2 and must not block dealer payments.
    isError: sellerAsSupplier.isError || (isSellerAsSupplier && seller.isError),
  };
}

interface SellerBankAccountsArgs {
  /** Nil outside mode 2, which skips every lookup. */
  businessCase: PaymentTenantBankAccountsArgs['businessCase'];
  bank: Bank;
}

/**
 * Documents of a seller-as-supplier commission sale are issued in the seller's name, so their bank
 * account is the one the seller has in the commission purchase, never a dealer branch account.
 * Resolves sale business case -> brokerage business case -> its checkout -> the commission purchase
 * order, whose customer is the seller.
 *
 * A seller without a bank account is valid: the options are empty and the default is null. A
 * missing link in that chain is not, and is reported as an error instead of an accountless seller.
 */
function useSellerBankAccounts(args: SellerBankAccountsArgs) {
  const brokerageBusinessCaseId = args.businessCase?.brokerageBusinessCaseId;
  const brokerageBusinessCaseQuery = useGetBusinessCaseQuery(
    isNotNil(brokerageBusinessCaseId) ? {businessCaseId: brokerageBusinessCaseId} : skipToken
  );
  const brokerageBusinessCase = brokerageBusinessCaseQuery.currentData;
  const brokerageCheckoutId = brokerageBusinessCase?.checkoutId;
  const brokerageCheckoutQuery = useGetCheckoutV2Query(
    isNotNil(brokerageCheckoutId) ? {checkoutId: brokerageCheckoutId} : skipToken
  );
  const brokerageCheckout = brokerageCheckoutQuery.currentData;

  const purchaseOrder = brokerageCheckout?.orders.find(
    (order) => order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE'
  );
  const bankAccounts =
    purchaseOrder?.contractInformation.customerContractInformation.bankAccounts ?? [];
  // Payments are served oldest first, so the last one carries the seller's current choice.
  const purchaseBankAccountId = findLast(
    (payment) => isNotNil(payment.customerBankAccountId),
    purchaseOrder?.payments ?? []
  )?.customerBankAccountId;

  const findBankAccount = (bankAccountId: string | Nullish) =>
    find((bankAccount) => bankAccount.id === bankAccountId, bankAccounts);

  const getTenantBankAccount = (bankAccountId: string | Nullish): TenantBankAccount | null => {
    const bankAccountData = findBankAccount(bankAccountId)?.bankAccountData;

    // Without a national number the backend cannot build the document's bank account, so the
    // document is issued without one, as it is for a seller who has no account at all.
    if (isNil(bankAccountData) || isNilOrEmpty(bankAccountData.number)) {
      return null;
    }

    return {
      accountName: bankAccountData.ownerName ?? bankAccountData.name ?? '',
      // The backend validates a national account number as `number/bankCode`.
      accountNumber: combineAccountNumberAndBankCode(
        bankAccountData.number ?? '',
        bankAccountData.bankCode ?? ''
      ),
      bankName: args.bank.getBankName(bankAccountData.bankCode),
      currencyCode: bankAccountData.currency,
      iban: bankAccountData.iban,
      swiftBic: bankAccountData.swiftBic,
      isPrimary: false,
    };
  };

  const isResolving = [brokerageBusinessCaseQuery, brokerageCheckoutQuery].some(
    (query) => query.isFetching && isNil(query.currentData)
  );
  const isBrokenLink =
    (isNotNil(args.businessCase) && isNil(brokerageBusinessCaseId)) ||
    (isNotNil(brokerageBusinessCase) && isNil(brokerageCheckoutId)) ||
    (isNotNil(brokerageCheckout) && isNil(purchaseOrder));

  return {
    options: bankAccounts.map(
      (bankAccount): BankAccountOption => ({
        label: args.bank.composeBankAccount(bankAccount.bankAccountData) ?? '',
        value: bankAccount.id,
      })
    ),
    defaultValue: findBankAccount(purchaseBankAccountId)?.id ?? head(bankAccounts)?.id ?? null,
    getTenantBankAccount,
    isLoading: isNotNil(args.businessCase) && isResolving,
    isError:
      brokerageBusinessCaseQuery.isError ||
      brokerageCheckoutQuery.isError ||
      args.bank.isBankListError ||
      isBrokenLink,
  };
}

const toTenantBankAccount = (
  bankAccount: ReturnType<Bank['getBankAccountByNumber']>
): TenantBankAccount | null =>
  bankAccount
    ? {
        accountName: bankAccount.accountName ?? '',
        accountNumber: bankAccount.accountNumber ?? '',
        bankName: bankAccount.bankName ?? '',
        currencyCode: bankAccount.currency,
        iban: bankAccount.iban,
        swiftBic: bankAccount.swift,
        isPrimary: bankAccount.isPrimary ?? false,
      }
    : null;
